import { useEffect, useState } from 'react';
import type { WorkflowListItem } from '@easyflow/shared';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { apiClient } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { formatTriggerLabel } from '@/components/workflow/workflow-shared';
import { CampaignBindingInfo, CampaignDraftRulesSummary, WorkflowDefinitionLink, WorkflowActionSummary } from './CampaignWorkflowInfo';
import type { useCampaignWorkflowDraft } from './useCampaignWorkflowDraft';

export function CampaignWorkflowDraftPanel({ draft, campaignId }: {
  draft: ReturnType<typeof useCampaignWorkflowDraft>; campaignId?: string;
}) {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [items, setItems] = useState<WorkflowListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let current = true;
    setLoading(true); setError(null);
    const timer = setTimeout(() => {
      void apiClient.getWorkflowsList({ search: search.trim(), limit: 20, offset, campaign_id: campaignId }).then(result => {
        if (current) { setItems(result.workflows); setTotal(Number(result.total)); }
      }).catch(error => { if (current) setError(getErrorMessage(error)); })
        .finally(() => { if (current) setLoading(false); });
    }, 250);
    return () => { current = false; clearTimeout(timer); };
  }, [search, offset, campaignId, attempt]);
  return <div className="space-y-4">
    <p className="text-sm">Aggiunte, rimozioni e sostituzioni sono <strong>da salvare</strong> con Salva Campagna. Gli stop immediati sono nel dettaglio e nelle Impostazioni CRM.</p>
    <p className="text-xs text-muted-foreground">Le regole non selezionate per la sostituzione continuano a convivere. Stop, errore e dissociazione non ripristinano le regole sostituite.</p>
    {draft.loading && <p role="status">Caricamento collegamenti…</p>}
    {draft.error && <div role="alert"><p>Collegamenti non caricati: {draft.error}. Le modifiche ai collegamenti sono bloccate.</p><Button variant="outline" onClick={draft.retry}>Riprova</Button></div>}
    {draft.ready && <>
      <h3 className="font-medium">Workflow collegati {draft.dirty && <Badge variant="outline">Da salvare</Badge>}</h3>
      {draft.ids.length === 0 && <p className="text-sm text-muted-foreground">Nessun workflow nella selezione.</p>}
      <div className="divide-y divide-border">
        {draft.ids.map(id => {
          const binding = draft.state?.triggers.find(link => link.workflow_id === id && !link.dissociated_at);
          const definition = draft.added.find(item => item.id === id);
          const consultation = binding ?? definition;
          return <div key={id} className="py-3 space-y-2">
            {binding ? <CampaignBindingInfo link={binding} /> : <p className="font-medium break-words">{definition?.name ?? id} <Badge variant="outline">Da salvare</Badge></p>}
            <div className="flex flex-wrap gap-2">
              {consultation && <WorkflowDefinitionLink definition={consultation} />}
              <Button size="sm" variant="outline" onClick={() => draft.remove(id)}>Rimuovi dalla bozza</Button>
            </div>
            <details className="text-sm">
              <summary className="cursor-pointer rounded focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">Scegli esplicitamente le regole da sostituire ({draft.replacements[id]?.length ?? 0} da salvare)</summary>
              <p className="text-xs text-muted-foreground my-2">Solo per questa campagna. Nessuna equivalenza automatica fra regola e grafo; il ripristino delle sostituzioni già salvate è un comando separato.</p>
              {(draft.state?.rules ?? []).filter(rule => rule.applicable).map(rule => <label key={rule.rule_id} className="flex gap-2 items-start py-1">
                <input type="checkbox" checked={(!!binding && rule.suspended_by_link_id === binding.id && !rule.restored_at) || (draft.replacements[id]?.includes(rule.rule_id) ?? false)}
                  disabled={!!binding && rule.suspended_by_link_id === binding.id && !rule.restored_at && !rule.replacement_requires_confirmation}
                  onChange={event => draft.replace(id, rule.rule_id, event.target.checked)} />
                <span>{rule.rule_name}{rule.suspended_by_link_id && !rule.restored_at ? ' · già sostituita' : ''}</span>
              </label>)}
              {!campaignId && <p>Salva prima la campagna per scegliere le regole effettivamente applicabili.</p>}
            </details>
          </div>;
        })}
      </div>
    </>}
    <details><summary className="cursor-pointer font-medium focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">Regole semplici: stato salvato</summary>
      <CampaignDraftRulesSummary draft={draft} />
    </details>
    <div className="border-t pt-3 space-y-2">
      <h3 className="font-medium">Catalogo autorizzato</h3>
      <Input aria-label="Cerca workflow nel catalogo" value={search} onChange={event => { setSearch(event.target.value); setOffset(0); }} placeholder="Cerca per nome o descrizione…" />
      {loading ? <p role="status">Caricamento catalogo…</p> : error ? <div role="alert"><p>Catalogo non aggiornato: {error}</p><Button variant="outline" onClick={() => setAttempt(value => value + 1)}>Riprova catalogo</Button></div> : <>
        <p className="text-xs text-muted-foreground">{total} definizioni corrispondenti</p>
        {items.length === 0 && <p>{search.trim() ? 'Nessun risultato per questa ricerca.' : 'Catalogo vuoto.'}</p>}
        <div className="divide-y divide-border">
          {items.map(item => <details key={item.id} className="py-2 text-sm">
            <summary className="cursor-pointer rounded focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
              <span className="break-words font-medium">{item.name}</span> · v{item.version} · {item.managed_by_org_id ? 'Insegna' : 'Tenant'}
              <span className="block text-xs text-muted-foreground">{formatTriggerLabel(item.trigger_type)} · {draft.ids.includes(item.id) ? 'Già collegato nella selezione' : item.campaign_selection?.message ?? 'Eleggibilità non disponibile'}</span>
            </summary>
            <p className="my-2">{item.description || 'Nessuna descrizione'}</p>
            <WorkflowActionSummary nodes={item.nodes} />
            <p className="text-xs mb-2">Definizione {item.is_active ? 'abilitata' : 'disabilitata'} · {item.admission_scope === 'campaigns' ? 'Campagne collegate' : item.admission_scope === 'tenant' ? 'Autonomo tenant' : 'Ambito da confermare'} · {item.linked_campaigns_count} collegamenti complessivi.</p>
            <div className="flex gap-2 flex-wrap">
              <WorkflowDefinitionLink definition={item} />
              <Button size="sm" onClick={() => draft.add(item)} disabled={!draft.ready || !item.campaign_selection?.allowed || draft.ids.includes(item.id)}>Aggiungi alla bozza</Button>
            </div>
          </details>)}
        </div>
        <div className="flex justify-between gap-2">
          <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(value => Math.max(0, value - 20))}>Precedenti</Button>
          <Button size="sm" variant="outline" disabled={offset + items.length >= total} onClick={() => setOffset(value => value + 20)}>Successivi</Button>
        </div>
      </>}
    </div>
  </div>;
}
